// +----------------------------------------------------------------------
// | EB [ EB赋能开发者，助力企业发展 ]
// +----------------------------------------------------------------------
// | Copyright (c) 2016~2024 https://www.eb.com All rights reserved.
// +----------------------------------------------------------------------
// | Licensed EB并不是自由软件，未经许可不能去掉EB相关版权
// +----------------------------------------------------------------------
// | Author: EB Team <admin@eb.com>
// +----------------------------------------------------------------------
import { App, createApp, Component, ComponentPublicInstance, Ref } from 'vue';
import { ElMessage } from 'element-plus';
import 'element-plus/dist/index.css';
import uploadFromComponent from './index.vue';
import Cookies from 'js-cookie';

interface UploadInstance extends ComponentPublicInstance {
  visible: boolean;
  callback: (images: string[]) => void;
  isMore: string;
}

interface UploadPlugin {
  install: (app: App) => void;
}

let uploadInstance: UploadInstance | null = null;

const uploadFrom: UploadPlugin = {
  install(app: App) {
    // 创建上传组件实例
    const createUploadInstance = (): UploadInstance => {
      const container = document.createElement('div');
      document.body.appendChild(container);
      
      const uploadApp = createApp(uploadFromComponent);
      
      // 复制主应用的配置，确保新实例能访问到相同的上下文
      uploadApp.use(ElMessage);

      // 弹层是独立 createApp，不继承主应用。素材管理页组件在主应用内，
      // $modalForm / $modalSure 可用；活动页走 $modalUpload 时编辑、删除分类会报 is not a function
      Object.assign(uploadApp.config.globalProperties, app.config.globalProperties);
      
      // 复制主应用的组件注册
      const appContext = app._context as any;
      if (appContext.components) {
        Object.keys(appContext.components).forEach(name => {
          uploadApp.component(name, appContext.components[name]);
        });
      }
      
      // 复制主应用的指令注册
      if (appContext.directives) {
        Object.keys(appContext.directives).forEach(name => {
          uploadApp.directive(name, appContext.directives[name]);
        });
      }
      
      // 复制主应用的插件和注入
      if (appContext.provides) {
        Object.keys(appContext.provides).forEach(key => {
          uploadApp.provide(key, appContext.provides[key]);
        });
      }
      
      // 特别处理路由相关的注入
      if (appContext.provides && appContext.provides['Symbol(route location)']) {
        uploadApp.provide('Symbol(route location)', appContext.provides['Symbol(route location)']);
      }
      if (appContext.provides && appContext.provides['Symbol(router)']) {
        uploadApp.provide('Symbol(router)', appContext.provides['Symbol(router)']);
      }
      
      // 复制主应用的所有 provides（包括 Symbol 类型的 key）
      const provides = appContext.provides || {};
      Object.getOwnPropertySymbols(provides).forEach(symbol => {
        uploadApp.provide(symbol, provides[symbol]);
      });
      
      // 尝试直接复制路由相关的注入
      try {
        // 如果主应用有路由实例，直接复制
        if (app.config.globalProperties.$router) {
          uploadApp.provide('router', app.config.globalProperties.$router);
        }
        if (app.config.globalProperties.$route) {
          uploadApp.provide('route', app.config.globalProperties.$route);
        }
      } catch (error) {
        console.warn('Failed to copy router context:', error);
      }
      
      const instance = uploadApp.mount(container) as UploadInstance;
      
      return instance;
    };

    // 获取或创建全局实例
    const getUploadInstance = (): UploadInstance => {
      if (!uploadInstance) {
        uploadInstance = createUploadInstance();
      }
      return uploadInstance;
    };

    // 添加全局方法
    app.config.globalProperties.$modalUpload = function(callback: (images: string[]) => void, isMore: string = '') {
      const instance = getUploadInstance();
      instance.visible = true;
      instance.callback = callback;
      instance.isMore = isMore;
    };
  }
};

export default uploadFrom; 